import DigiobcanApi from '../../../api/DigiobcanApi';
import { Link } from '../../../components/Link';
import LinkButton from '../../../components/LinkButton';
import NotFound from '../../../components/NotFound';
import QuestionStep from '../../../components/Votings/QuestionStep';
import VotingReview from '../../../components/Votings/VotingReview';
import VotingRunChrome from '../../../components/Votings/VotingRunChrome';
import VotingStatusScreen from '../../../components/Votings/VotingStatusScreen';
import { getVotingDetail } from '../../../features/queries/getVotingDetail';
import { entityId } from '../../../lib/ids';
import { QueryNameSpace, useQ } from '../../../lib/query';
import {
  answerLines,
  answerRuleHint,
  isQuestionAnswered,
  toVotesPayload,
  votingDetailPath,
  votingResultsPath,
  votingResultsPublished,
  votingState,
  type AnswerOption,
  type VotingAnswers,
} from '../../../lib/votings';
import { useLogger } from '../../../providers/LoggerProvider';
import {
  __,
  copyToClipboard,
  formatDateTime,
  toastSuccess,
} from '../../../utils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { useAuth } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  LinkProvider,
  Skeleton,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate, useParams } from 'react-router';

/** Ako sa hlasovanie skončilo. `undefined` znamená, že ešte beží. */
type Outcome =
  | { kind: 'submitted'; at: Date }
  | { kind: 'duplicate' }
  | { kind: 'closed'; at: Date }
  | { kind: 'offline'; at: Date }
  | { kind: 'inactive' }
  | { kind: 'verified' };

const STEP_WIDTH = 640;

/**
 * Sústredený režim hlasovania - jedna otázka na obrazovku, kontrola pred
 * odoslaním, potvrdenie.
 *
 * Prečo je to vlastná stránka a nie tab detailu: hlasovanie je jediná úloha
 * v appke, ktorá sa nedá odložiť a vrátiť sa k nej (odosiela sa raz a natrvalo),
 * takže kým beží, nemá byť kam odbočiť. Detail (`/hlasovania/:id`) zostáva
 * v shelli a je to on, kto o hlasovaní **informuje**; sem sa z neho vchádza
 * tlačidlom „Hlasovať".
 *
 * Stavy, v ktorých sa hlasovať nedá, sú tu tiež - priamy odkaz na túto adresu
 * musí zniesť aj hlasovanie, ktoré sa medzitým zavrelo. Sú to obrazovky
 * s jednou akciou, nie prekrytie nad vypnutým formulárom.
 */
const VotingRunPage: React.FC = () => {
  const { votingId: votingIdParam } = useParams() as { votingId: string };
  const votingId = entityId(votingIdParam, 'voting');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logger } = useLogger();
  const { authState } = useAuth();
  // `RecaptchaWrapper` mountuje reCAPTCHA len pre neprihláseného.
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [answers, setAnswers] = React.useState<VotingAnswers>({});
  const [step, setStep] = React.useState(0);
  const [onReview, setOnReview] = React.useState(false);
  const [showMissing, setShowMissing] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [outcome, setOutcome] = React.useState<Outcome>();

  const {
    data: detail,
    isLoading,
    isError,
    error,
  } = useQ(
    getVotingDetail,
    { votingId },
    // Rovnaké okno ako v [[VotingsPage]]: kým `AuthProvider` nedobehne, token
    // na klientovi nie je a server by neverejné hlasovanie nevydal.
    { refetchOnMount: 'always', enabled: authState !== 'authorizing' },
  );

  // Stabilná referencia: `detail?.questions ?? []` vyrába nové pole na každý
  // render, takže by `useMemo` nižšie prepočítaval vždy.
  const questions = React.useMemo(
    () => detail?.questions ?? [],
    [detail?.questions],
  );
  const question = questions[step];

  const leave = React.useCallback(
    () => navigate(votingDetailPath(votingId)),
    [navigate, votingId],
  );

  const setAnswer = React.useCallback(
    (questionId: number, next: AnswerOption[]) => {
      setAnswers((current) => ({ ...current, [questionId]: next }));
      setShowMissing(false);
    },
    [],
  );

  const answeredCount = React.useMemo(
    () =>
      questions.filter((candidate) =>
        isQuestionAnswered(candidate, answers[candidate.question_id]),
      ).length,
    [questions, answers],
  );

  const submit = React.useCallback(async () => {
    if (!detail) return;

    if (!navigator.onLine) {
      setOutcome({ kind: 'offline', at: new Date() });
      return;
    }

    // Anonymný hlas musí priniesť reCAPTCHA token; prihlásený ho neposiela.
    let recaptchaToken: string | undefined;
    if (authState === 'unauthorized') {
      if (!executeRecaptcha) {
        setOutcome({ kind: 'offline', at: new Date() });
        return;
      }
      recaptchaToken = await executeRecaptcha('submit_vote');
      if (!recaptchaToken) {
        setOutcome({ kind: 'offline', at: new Date() });
        return;
      }
    }

    setSubmitting(true);
    try {
      await DigiobcanApi.submitVote(
        votingId,
        {},
        {
          votes: toVotesPayload(answers),
          ...(recaptchaToken ? { recaptchaToken } : {}),
        },
      );
      setOutcome({ kind: 'submitted', at: new Date() });

      /**
       * Odovzdaný hlas mení **tri** obrazovky naraz a žiadna z nich nie je
       * táto: zoznam (počet hlasov), úvod (`already_voted` a s ním celá
       * pätička) a výsledky. Bez invalidácie ich react-query drží ešte minútu
       * v pôvodnom stave, takže sa občan po odoslaní vrátil na zoznam,
       * kde ho hlasovanie znova pozývalo hlasovať - a druhý pokus skončil
       * na 409.
       *
       * Nečaká sa na dobehnutie: obrazovka s potvrdením už stojí a refetch jej
       * nemá čo dodať.
       */
      queryClient.invalidateQueries({
        queryKey: [QueryNameSpace.getVotings],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryNameSpace.getVotingDetail],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryNameSpace.getVotingResults],
      });
    } catch (e) {
      const status = e?.details?.statusCode;

      if (status === 409) setOutcome({ kind: 'duplicate' });
      else if (status === 410) setOutcome({ kind: 'closed', at: new Date() });
      else if (status === 400) setOutcome({ kind: 'inactive' });
      else if (status === 401 || status === 403) {
        // Prihlásenie aj overenie účtu vysvetľuje úvod, nie táto obrazovka -
        // občan sa má dozvedieť **pred** vypĺňaním, čo mu chýba.
        setOutcome({ kind: 'verified' });
      } else {
        logger.error('Failed to submit vote', {
          action: 'submitVote',
          error: e,
          data: { votingId, votes: toVotesPayload(answers) },
        });
        setOutcome({ kind: 'offline', at: new Date() });
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    answers,
    authState,
    detail,
    executeRecaptcha,
    logger,
    queryClient,
    votingId,
  ]);

  if (
    isError &&
    authState !== 'authorizing' &&
    error?.details?.statusCode === 404
  ) {
    return (
      <NotFound
        message={__('Hlasovanie sa nenašlo.')}
        backUrl='/hlasovania'
        backText={__('Späť na hlasovania')}
      />
    );
  }

  const title = detail?.title ?? __('Hlasovanie');

  // Odkaz na výsledky sa nekreslí hlasovaniu, ktoré ich nezverejňuje - to
  // isté pravidlo drží detail aj zoznam ([[votings]]).
  const resultsPublished = !!detail && votingResultsPublished(detail);

  const resultsAction =
    resultsPublished &&
    detail &&
    (detail.available_incomplete_results || detail.already_voted) ? (
      <Typography
        component={Link}
        to={votingResultsPath(votingId)}
        variant='body2'
        sx={{
          color: 'blue.dark',
          textDecoration: 'none',
          display: { xs: 'none', sm: 'inline' },
        }}
      >
        {__('Priebežné výsledky')}
      </Typography>
    ) : undefined;

  const backToVotings = (
    <LinkButton variant='outline' to='/hlasovania'>
      {__('Späť na hlasovania')}
    </LinkButton>
  );
  const showResults = resultsPublished ? (
    <LinkButton to={votingResultsPath(votingId)}>
      {__('Zobraziť výsledky')}
    </LinkButton>
  ) : null;

  /** Obrazovka s jednou akciou - buď výsledok odoslania, alebo dôvod, prečo sa nedá hlasovať. */
  const statusScreen = (): React.ReactNode => {
    if (!detail) return null;

    if (outcome?.kind === 'submitted') {
      return (
        <VotingStatusScreen
          tone='success'
          icon={<CheckIcon sx={{ fontSize: 30 }} />}
          title={__('Váš hlas je odovzdaný')}
          description={`${__('Odpovede sme zaznamenali')} ${formatDateTime(outcome.at)}. ${__('Meniť sa už nedajú.')}`}
          actions={
            <>
              {showResults}
              {backToVotings}
            </>
          }
        />
      );
    }

    if (outcome?.kind === 'duplicate' || (!outcome && detail.already_voted)) {
      return (
        <VotingStatusScreen
          tone='error'
          icon={<PriorityHighIcon sx={{ fontSize: 30 }} />}
          title={__('Tento hlas sme nezapísali')}
          description={__(
            'Z vášho účtu už v tomto hlasovaní hlas prišiel. Každý účet hlasuje raz.',
          )}
          actions={
            <>
              <LinkButton to={votingDetailPath(votingId)}>
                {__('Zobraziť moje odpovede')}
              </LinkButton>
              {resultsPublished && (
                <LinkButton variant='outline' to={votingResultsPath(votingId)}>
                  {__('Zobraziť výsledky')}
                </LinkButton>
              )}
            </>
          }
        />
      );
    }

    if (outcome?.kind === 'closed' || outcome?.kind === 'inactive') {
      const filled = questions
        .map((candidate) => ({
          title: candidate.title,
          lines: answerLines(candidate, answers[candidate.question_id]).filter(
            (line) => line.filled,
          ),
        }))
        .filter((entry) => entry.lines.length > 0);

      return (
        <VotingStatusScreen
          tone='neutral'
          icon={<ScheduleIcon sx={{ fontSize: 26 }} />}
          title={__('Hlasovanie sa zavrelo pred odoslaním')}
          description={
            detail.open_to
              ? `${__('Skončilo')} ${formatDateTime(detail.open_to)}, ${__('teda kým ste vypĺňali. Váš hlas sa už nedá zapísať a výsledok ho neobsahuje.')}`
              : __(
                  'Hlasovanie sa medzitým zavrelo. Váš hlas sa už nedá zapísať a výsledok ho neobsahuje.',
                )
          }
          code={
            outcome.kind === 'closed'
              ? `VOTING_CLOSED · ${formatDateTime(outcome.at)}`
              : 'VOTING_NOT_ACTIVE'
          }
          actions={
            <>
              {showResults}
              {backToVotings}
            </>
          }
        >
          {/* Jediný stav, v ktorom občan stratí prácu - tak nech ju aspoň
              vidí a vie si ju odniesť. */}
          {filled.length > 0 && (
            <Stack
              gap={1.25}
              sx={({ palette, shape }) => ({
                width: '100%',
                textAlign: 'left',
                border: `1px solid ${palette.divider}`,
                borderRadius: `${shape.borderRadius}px`,
                backgroundColor: palette.grey[50],
                p: 2,
              })}
            >
              <Typography
                variant='overline'
                sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}
              >
                {__('Čo ste mali vyplnené')}
              </Typography>
              <Stack gap={1}>
                {filled.map((entry) => (
                  <Stack key={entry.title} gap={0.25}>
                    <Typography variant='caption' color='text.secondary'>
                      {entry.title}
                    </Typography>
                    <Typography sx={{ fontSize: 14, lineHeight: 1.45 }}>
                      {entry.lines.map((line) => line.text).join(', ')}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Button
                variant='outline'
                size='small'
                startIcon={<ContentCopyIcon fontSize='small' />}
                sx={{ alignSelf: 'flex-start' }}
                onClick={() =>
                  copyToClipboard(
                    filled
                      .map(
                        (entry) =>
                          `${entry.title}\n${entry.lines.map((line) => line.text).join('\n')}`,
                      )
                      .join('\n\n'),
                    { onSuccess: () => toastSuccess(__('Skopírované.')) },
                  )
                }
              >
                {__('Skopírovať odpovede')}
              </Button>
            </Stack>
          )}
        </VotingStatusScreen>
      );
    }

    if (outcome?.kind === 'offline') {
      return (
        <VotingStatusScreen
          tone='warning'
          icon={<WifiOffIcon sx={{ fontSize: 26 }} />}
          title={__('Odoslanie zlyhalo')}
          description={__(
            'Odpovede sme si nechali - po obnovení pripojenia stačí odoslať znova.',
          )}
          code={`VOTE_SUBMIT_OFFLINE · ${formatDateTime(outcome.at)}`}
          actions={
            <Button loading={submitting} onClick={submit}>
              {__('Odoslať znova')}
            </Button>
          }
        />
      );
    }

    if (outcome?.kind === 'verified') {
      return (
        <VotingStatusScreen
          tone='warning'
          icon={<LockOutlinedIcon sx={{ fontSize: 26 }} />}
          title={__('Hlas sa nedal odoslať z tohto účtu')}
          description={__(
            'V tomto hlasovaní môžu hlasovať iba overené alebo prihlásené účty. Podmienky nájdete v zadaní hlasovania.',
          )}
          actions={
            <LinkButton to={votingDetailPath(votingId)}>
              {__('Zobraziť zadanie')}
            </LinkButton>
          }
        />
      );
    }

    // --- Dôvody, prečo sa nedá začať --------------------------------------
    const state = votingState(detail, new Date());

    if (state === 'draft') {
      return (
        <VotingStatusScreen
          icon={<ScheduleIcon sx={{ fontSize: 26 }} />}
          title={__('Hlasovanie sa ešte nezačalo')}
          description={
            detail.open_from
              ? `${__('Otvára sa')} ${formatDateTime(detail.open_from)}. ${__('Do vtedy si môžete prečítať zadanie.')}`
              : __(
                  'Zatiaľ nie je otvorené. Do vtedy si môžete prečítať zadanie.',
                )
          }
          actions={
            <>
              <LinkButton to={votingDetailPath(votingId)}>
                {__('Prečítať zadanie')}
              </LinkButton>
              {backToVotings}
            </>
          }
        />
      );
    }

    if (state === 'closed') {
      return (
        <VotingStatusScreen
          icon={<LockOutlinedIcon sx={{ fontSize: 26 }} />}
          title={__('Hlasovanie je uzavreté')}
          description={
            detail.open_to
              ? `${__('Skončilo')} ${formatDateTime(detail.open_to)}.`
              : undefined
          }
          actions={
            <>
              {showResults}
              {backToVotings}
            </>
          }
        />
      );
    }

    return null;
  };

  const status = statusScreen();

  const canContinue = question
    ? isQuestionAnswered(question, answers[question.question_id])
    : false;

  const goNext = () => {
    if (!canContinue) {
      setShowMissing(true);
      return;
    }
    setShowMissing(false);
    if (step === questions.length - 1) setOnReview(true);
    else setStep((current) => current + 1);
  };

  const footer = (() => {
    if (status || !detail) return null;

    if (onReview) {
      return (
        <Stack direction='row' alignItems='center' gap={1.5}>
          <Button
            variant='outline'
            size='large'
            onClick={() => setOnReview(false)}
          >
            {__('Späť k otázkam')}
          </Button>
          {detail.open_to && (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ display: { xs: 'none', md: 'block' } }}
            >
              {`${__('Hlasovanie je otvorené do')} ${formatDateTime(detail.open_to)}`}
            </Typography>
          )}
          <Button
            size='large'
            sx={{ ml: 'auto' }}
            loading={submitting}
            onClick={submit}
          >
            {__('Odoslať hlasovanie')}
          </Button>
        </Stack>
      );
    }

    const isLast = step === questions.length - 1;
    const isFreeText = question?.type === 'free_text';

    return (
      <Stack direction='row' alignItems='center' gap={1.5}>
        <Button
          variant='outline'
          size='large'
          disabled={step === 0}
          startIcon={<ArrowBackIcon fontSize='small' />}
          onClick={() => {
            setShowMissing(false);
            setStep((current) => Math.max(0, current - 1));
          }}
        >
          <Box component='span' sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {__('Späť')}
          </Box>
        </Button>

        {/* Otvorená odpoveď je nepovinná, takže má vlastnú cestu ďalej -
            inak by „Ďalej" pri prázdnom poli vyzeralo ako chyba. */}
        {isFreeText && !canContinueText(question, answers) && (
          <Button variant='text' size='large' onClick={goNext}>
            {__('Preskočiť')}
          </Button>
        )}

        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ display: { xs: 'none', md: 'block' } }}
        >
          {question && answerRuleHint(question)}
        </Typography>

        <Button
          size='large'
          sx={{ ml: 'auto' }}
          disabled={!canContinue}
          endIcon={!isLast ? <ArrowForwardIcon fontSize='small' /> : undefined}
          onClick={goNext}
        >
          {isLast ? __('Skontrolovať odpovede') : __('Ďalej')}
        </Button>
      </Stack>
    );
  })();

  return (
    // Shell sa nad touto adresou nekreslí, takže `LinkProvider` si musí
    // nainštalovať stránka sama - inak by odkazy v nej načítali celú appku.
    <LinkProvider navigate={navigate}>
      <VotingRunChrome
        title={title}
        progress={
          status || !questions.length
            ? undefined
            : onReview
              ? 1
              : (step + 1) / (questions.length + 1)
        }
        onLeave={leave}
        resultsAction={resultsAction}
        footer={footer}
      >
        {isLoading || authState === 'authorizing' ? (
          <Stack
            sx={{
              p: 4,
              gap: 2,
              maxWidth: STEP_WIDTH,
              mx: 'auto',
              width: '100%',
            }}
          >
            <Skeleton variant='rounded' height={40} />
            <Skeleton variant='rounded' height={220} />
          </Stack>
        ) : status ? (
          status
        ) : onReview ? (
          <Box
            sx={({ palette }) => ({
              flex: 1,
              backgroundColor: palette.grey[100],
              px: { xs: 1.75, md: 3 },
              py: { xs: 2.25, md: 5 },
            })}
          >
            <Box sx={{ maxWidth: 680, mx: 'auto' }}>
              <VotingReview
                questions={questions}
                answers={answers}
                onEdit={(index) => {
                  setStep(index);
                  setOnReview(false);
                  setShowMissing(true);
                }}
              />
            </Box>
          </Box>
        ) : question ? (
          <Stack
            gap={{ xs: 2.25, md: 3.5 }}
            sx={{
              width: '100%',
              maxWidth: STEP_WIDTH,
              mx: 'auto',
              px: { xs: 2, md: 0 },
              py: { xs: 3, md: 7 },
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              gap={1.25}
              flexWrap='wrap'
            >
              <Typography
                variant='overline'
                sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}
              >
                {`${__('Otázka')} ${step + 1} ${__('z')} ${questions.length}`}
              </Typography>
              <Box
                component='span'
                sx={({ palette, shape }) => ({
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 24,
                  px: 1,
                  borderRadius: `${shape.borderRadiusSm}px`,
                  backgroundColor: palette.grey[100],
                  fontSize: 12,
                  fontWeight: 500,
                  color: palette.grey[600],
                })}
              >
                {answerRuleHint(question)}
              </Box>
            </Stack>

            <Stack gap={1.25}>
              <Typography
                component='h2'
                sx={{
                  fontSize: { xs: 22, md: 30 },
                  fontWeight: 700,
                  lineHeight: 1.25,
                  letterSpacing: '-0.01em',
                }}
              >
                {question.title}
              </Typography>
              {question.description && (
                <Typography
                  variant='body1'
                  color='text.secondary'
                  sx={{ lineHeight: 1.6 }}
                >
                  {question.description}
                </Typography>
              )}
            </Stack>

            <QuestionStep
              question={question}
              value={answers[question.question_id] ?? []}
              onChange={(next) => setAnswer(question.question_id, next)}
              showMissing={showMissing}
            />

            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ display: { md: 'none' } }}
            >
              {`${__('Vyplnené')} ${answeredCount} ${__('z')} ${questions.length}`}
            </Typography>
          </Stack>
        ) : null}
      </VotingRunChrome>
    </LinkProvider>
  );
};

/** Má otvorená odpoveď text? Rozhoduje o tom, či sa v pätičke ponúka „Preskočiť". */
const canContinueText = (
  question: { question_id: number },
  answers: VotingAnswers,
) => !!answers[question.question_id]?.[0]?.text?.trim();

export default VotingRunPage;
